/**
 * useScrollRestoration (TMMPDM-0077, NAV-010) — Back returns to where the person was.
 *
 * The scroll offset of each history entry is remembered in memory, keyed by `location.key`, while
 * the person scrolls. It is UI state inside the page's lifetime: never stored (no storage of any
 * kind), gone on a reload, when the browser's own restoration is all there is.
 *
 * On a Back or Forward (POP) the remembered offset is restored once, after the page settles. Pages
 * read their data on mount, so at `popstate` the page is a short skeleton and any offset would be
 * clamped near the top (the browser's own restoration is, too). The page has settled when it is tall
 * enough to reach the remembered offset (its data has drawn: a skeleton cannot reach an offset the
 * list once had), nothing in `main` is `aria-busy`, and it has stopped changing (content and
 * height) for `SCROLL_QUIET_MS`. A page that never settles — or never grows that tall, because
 * there is less to show now — is restored at `SCROLL_MAX_WAIT_MS` regardless, clamped. The person
 * scrolling or interacting first cancels the restore — it never fights them. A PUSH or REPLACE is
 * not handled here (AppLayout scrolls to the top).
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/** Quiet time with no change in `main` before the page counts as settled. */
export const SCROLL_QUIET_MS = 150;
/** The longest a restore waits for the page to settle. */
export const SCROLL_MAX_WAIT_MS = 4000;
/** Entries remembered; the oldest go first (a history is not unbounded in practice). */
const MAX_ENTRIES = 100;

/** What the person does to scroll or take over: a wheel, a touch, a key, a press. */
const INTERACTIONS = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;

const positions = new Map<string, number>();

function remember(key: string, offset: number) {
  positions.delete(key);
  positions.set(key, offset);
  if (positions.size > MAX_ENTRIES) {
    const oldest = positions.keys().next().value;
    if (oldest !== undefined) positions.delete(oldest);
  }
}

/** Test seam: forget every remembered offset. */
export function __resetScrollMemory() {
  positions.clear();
}

export function useScrollRestoration(mainRef: RefObject<HTMLElement | null>) {
  const location = useLocation();
  const navigationType = useNavigationType();
  const currentKey = useRef(location.key);
  // The entry whose offset is waiting to be restored: its scrolls (the browser's own clamped
  // restoration among them) are not the person's position and are not remembered.
  const restoring = useRef<string | null>(null);

  useEffect(() => {
    const onScroll = () => {
      if (restoring.current === currentKey.current) return;
      remember(currentKey.current, window.scrollY);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Layout effect: the entry is switched in the commit that shows its page, before the scroll events
  // that commit causes are delivered, so the entry being left keeps the offset it was left at.
  useLayoutEffect(() => {
    currentKey.current = location.key;
    restoring.current =
      navigationType === 'POP' && (positions.get(location.key) ?? 0) > 0 ? location.key : null;
  }, [location.key, navigationType]);

  useEffect(() => {
    const key = location.key;
    const target = navigationType === 'POP' ? (positions.get(key) ?? 0) : 0;
    const main = mainRef.current;
    if (target <= 0 || !main) {
      if (restoring.current === key) restoring.current = null;
      return;
    }
    let quiet: number | undefined;
    const stop = () => {
      window.clearTimeout(quiet);
      window.clearTimeout(cap);
      mutations.disconnect();
      resizes?.disconnect();
      for (const type of INTERACTIONS) window.removeEventListener(type, cancel, true);
      if (restoring.current === key) restoring.current = null;
    };
    // The person got there first: this offset is no longer theirs to be taken back to.
    const cancel = () => stop();
    const restore = () => {
      stop();
      window.scrollTo(0, target);
    };
    const check = () => {
      window.clearTimeout(quiet);
      quiet = undefined;
      // Still loading, or not yet tall enough: the next change (the busy marker going, the rows
      // drawing) looks again.
      if (main.querySelector('[aria-busy="true"]')) return;
      if (document.documentElement.scrollHeight - window.innerHeight < target) return;
      quiet = window.setTimeout(restore, SCROLL_QUIET_MS);
    };
    const mutations = new MutationObserver(check);
    mutations.observe(main, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-busy'],
    });
    const resizes = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(check);
    resizes?.observe(main);
    const cap = window.setTimeout(restore, SCROLL_MAX_WAIT_MS);
    for (const type of INTERACTIONS) window.addEventListener(type, cancel, true);
    check();
    return stop;
  }, [location.key, navigationType, mainRef]);
}
